import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardCard } from 'DashboardCard';
import { type RecommendedClassmatesData, careerProfilesApi } from 'CareerProfiles';
import { type navigationHelper } from 'NavigationHelpers';
import { Text } from 'Text';
import { Avatar } from 'Avatar';
import { locationString } from 'Location';
import { useNavigationHelper } from 'FrontRoyalAngular/useNavigationHelper';
import { useRequiredCurrentUser } from 'FrontRoyalAngular';
import { getUserProgramState } from 'Users';

const { useGetRecommendedClassmatesQuery } = careerProfilesApi;

type MeetYourClassProps = {
    loadRoute: ReturnType<typeof navigationHelper>['loadRoute'];
    classmates?: RecommendedClassmatesData[];
    isLoading: boolean;
};

export function MeetYourClass() {
    const { loadRoute } = useNavigationHelper();
    const currentUser = useRequiredCurrentUser();
    const cohortId = useMemo(() => getUserProgramState(currentUser)?.cohort?.id ?? null, [currentUser]);
    const { data, isLoading } = useGetRecommendedClassmatesQuery(cohortId);

    return <MeetYourClassPresentation loadRoute={loadRoute} classmates={data} isLoading={isLoading} />;
}

export function MeetYourClassPresentation({ loadRoute, classmates = [], isLoading }: MeetYourClassProps) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'featured_students.featured_students' });

    if (!isLoading && classmates.length === 0) return null;

    return (
        <DashboardCard
            headingText={t('meet_your_class')}
            handleViewAllPress={() => loadRoute('/student-network?myClass')}
            isLoading={isLoading}
            cardClassNames={{ body: 'p-2.5' }}
        >
            <div className="grid grid-cols-1 gap-x-2.5 gap-y-[5px] xs:grid-cols-2">
                {classmates?.map((student, index) => (
                    <button
                        key={student.userId}
                        className="flex w-full items-start gap-3 rounded-5 p-2.5 text-left duration-300 ease-in-out transition-background hover:bg-slate-50"
                        onClick={() => loadRoute(`/student-network?ids=${student.userId}`)}
                        type="button"
                    >
                        <Avatar
                            src={student.avatarUrl ?? undefined}
                            name={student.name ?? undefined}
                            showFallback
                            tabIndex={index}
                        />
                        <div className="flex flex-col">
                            <Text color="black" weight="semibold" className="mb-1 text-start text-base leading-5">
                                {student.name}
                            </Text>
                            <Text color="black" weight="normal" className="mb-1 text-start text-xs leading-4">
                                {student.company ? `${student.jobTitle}, ${student.company}` : student.jobTitle}
                            </Text>
                            <Text color="slate-grey" weight="normal" className="text-start text-xs leading-4">
                                {locationString(student.placeDetails, student.city, student.state, student.country)}
                            </Text>
                        </div>
                    </button>
                ))}
            </div>
        </DashboardCard>
    );
}
